import { sample, random, range, shuffle } from "lodash";

import { Sound } from "./core";
import { audioCtx, impulseResponse, analyser } from "./audio";

// Sound categorisation (for installation)
// Instruct no words in response please.
// Quality: Instruct to find a quiet place.
// Schedule: Month for sound collection. At least internally decide.
// Schedule: Tytti away 2.8. - 13.8.
// 8.8. recording session in Helsinki
// Eri tiheyksiä: Vaikka esim. kaksiäänisyys, ja erilaisilla tiheyksillä, ja välillä ilman kasvua.
// Kun laulaa melodiaa, tulos on erilainen
// Muiden ääniä? Ehkä ei paljoa tarvitse mutta kokeillaan vähän.

// Installaatio:
// - Kuinka pitkään yksittäinen ihminen? Mitä tapahtuu?
// - Ensin sointuja, sitten melodiaa, rytmiä.

const CHORD_INTERVALS = [
  [0, 4, 7, 12], // major third
  [0, 4, 7, 11, 12], // major seventh
  [0, 3, 7, 10, 12], // minor seventh
  [0, 4, 7, 10, 12] // dominant seventh
];
const OCTAVE_SHIFT_PROBABILITY = 0.15;
const RESPONSE_DURATIONS = [6, 8, 10, 12, 14];
const EVENT_FADE_IN_TIME = 0.5;
const EVENT_FADE_OUT_TIME = 0.5;
const EVENT_MAX_GAIN = 0.7;
const EVENT_ONSET_DELAY_RANGES = [[0, 0.7], [0.2, 0.9], [0.4, 1.1]]; // [max delay, min delay]

const PANS = [-0.8, -0.4, 0, 0.4, 0.8];
const REVERB_WETS = [0.15, 0];
const DELAYS = [[0.1, 0], [0.4, 0.2], [0.4, 0.5], [0.2, 0.6], [0.1, 0.4]]; // [time, feedback amount]

let tracksPromise: Promise<AudioNode[]>;
function getTracks() {
  if (!tracksPromise) {
    tracksPromise = impulseResponse.then(irBuf => {
      let reverbs = REVERB_WETS.map(wetAmount => {
        let reverbWet = audioCtx.createGain();
        reverbWet.gain.value = wetAmount;
        let reverbDry = audioCtx.createGain();
        reverbDry.gain.value = 1 - wetAmount;
        if (wetAmount > 0) {
          let reverb = audioCtx.createConvolver();
          reverb.buffer = irBuf as AudioBuffer;
          reverbWet.connect(reverb);
          reverb.connect(analyser);
          reverbDry.connect(analyser);
          reverb.connect(audioCtx.destination);
          reverbDry.connect(audioCtx.destination);
        } else {
          reverbWet.connect(analyser);
          reverbDry.connect(analyser);
          reverbWet.connect(audioCtx.destination);
          reverbDry.connect(audioCtx.destination);
        }
        return { reverbWet, reverbDry };
      });

      return range(7).map(i => {
        let panAmount = PANS[i % PANS.length];
        let { reverbWet, reverbDry } = reverbs[i % reverbs.length];
        let [delayTime, delayAmount] = sample(DELAYS) as [number, number];

        let panner = audioCtx.createPanner();
        panner.panningModel = "equalpower";
        panner.setPosition(panAmount, 0, 1 - Math.abs(panAmount));

        let delayGain = audioCtx.createGain();
        delayGain.gain.value = delayAmount;
        let delay = audioCtx.createDelay();
        delay.delayTime.value = delayTime;

        panner.connect(delayGain);
        panner.connect(reverbDry);

        delayGain.connect(delay);
        delay.connect(delayGain);
        delay.connect(reverbWet);
        delay.connect(reverbDry);

        return panner;
      });
    });
  }
  return tracksPromise;
}

export interface ChoirResponsePlayStatus {
  onDone: Promise<any>;
  responseNumber: number;
}
export async function playChoirResponse(
  baseSound: Sound,
  responseSounds: Sound[],
  responseNumber: number,
  responsePlayCallback: () => void
): Promise<ChoirResponsePlayStatus> {
  let tracks = await getTracks();
  let rootNote = +baseSound.metadata.pitch;
  let intervals = sample(CHORD_INTERVALS) as number[];
  let notes = shuffle(intervals.map(i => rootNote + i));
  let allSounds = [baseSound].concat(responseSounds);
  let responseDuration = sample(RESPONSE_DURATIONS) as number;
  let [onsetDelayMin, onsetDelayMax] = sample(
    EVENT_ONSET_DELAY_RANGES
  ) as number[];

  let i = 0,
    t = 0;
  while (t < responseDuration) {
    let sound = allSounds[i % allSounds.length];
    let sourceNote = +sound.metadata.pitch;
    let targetNote = notes[i % notes.length];
    let distance = targetNote - sourceNote;
    while (Math.random() < OCTAVE_SHIFT_PROBABILITY) {
      distance += 12;
    }
    while (Math.random() < OCTAVE_SHIFT_PROBABILITY) {
      distance -= 12;
    }
    playSound(
      sound,
      distance,
      t,
      responseDuration,
      tracks,
      responsePlayCallback
    );

    i++;
    t += random(onsetDelayMin, onsetDelayMax);
  }
  let donePromise = new Promise(res =>
    setTimeout(res, responseDuration * 1000)
  );
  return {
    onDone: donePromise,
    responseNumber
  };
}

function playSound(
  sound: Sound,
  pitchShift: number,
  startAt: number,
  stopAt: number,
  tracks: AudioNode[],
  responsePlayCallback: () => void
) {
  setTimeout(() => {
    responsePlayCallback();

    let bufSrc = audioCtx.createBufferSource();
    bufSrc.buffer = sound.data;
    bufSrc.playbackRate.value = Math.pow(2, pitchShift / 12);
    bufSrc.start(audioCtx.currentTime);
    bufSrc.stop(audioCtx.currentTime + stopAt);

    let gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0, audioCtx.currentTime);
    gain.gain.linearRampToValueAtTime(
      EVENT_MAX_GAIN,
      audioCtx.currentTime + EVENT_FADE_IN_TIME
    );
    gain.gain.setValueAtTime(
      EVENT_MAX_GAIN,
      audioCtx.currentTime + stopAt - EVENT_FADE_OUT_TIME
    );
    gain.gain.linearRampToValueAtTime(0.001, audioCtx.currentTime + stopAt);
    bufSrc.connect(gain);
    gain.connect(sample(tracks) as AudioNode);
  }, startAt * 1000);
}
